import { Box, Button, SpaceBetween, Textarea } from "@amzn/awsui-components-react";
import { FC, useState } from "react";
import { MapEngineHandle } from "../../react";

export interface GeoJsonPanelProps {
    handle: MapEngineHandle | null;
}

const SOURCE_ID = "viewer-v2-geojson";
const LAYER_IDS = [`${SOURCE_ID}-fill`, `${SOURCE_ID}-line`, `${SOURCE_ID}-point`];
const OVERLAY_COLOR = "#ec7211";

/**
 * Paste a GeoJSON FeatureCollection / geometry to overlay it on the map. Adds
 * fill/line/point layers so any geometry type is visible. Clear removes the
 * overlay. Uses the v2 engine's overlay methods (no 1.0 store coupling).
 */
const GeoJsonPanel: FC<GeoJsonPanelProps> = ({ handle }) => {
    const [text, setText] = useState("");
    const [error, setError] = useState<string | null>(null);

    const plot = () => {
        const engine = handle?.engine();
        if (!engine) return;
        let data: unknown;
        try {
            data = JSON.parse(text);
        } catch {
            setError("Invalid JSON.");
            return;
        }
        setError(null);
        if (!engine.setGeoJsonSource(SOURCE_ID, data)) {
            setError("Map not ready.");
            return;
        }
        engine.addOverlayLayer({
            id: LAYER_IDS[0], type: "fill", source: SOURCE_ID,
            filter: ["==", "$type", "Polygon"],
            paint: { "fill-color": OVERLAY_COLOR, "fill-opacity": 0.3 },
        });
        engine.addOverlayLayer({
            id: LAYER_IDS[1], type: "line", source: SOURCE_ID,
            filter: ["==", "$type", "LineString"],
            paint: { "line-color": OVERLAY_COLOR, "line-width": 2 },
        });
        engine.addOverlayLayer({
            id: LAYER_IDS[2], type: "circle", source: SOURCE_ID,
            filter: ["==", "$type", "Point"],
            paint: { "circle-color": OVERLAY_COLOR, "circle-radius": 5 },
        });
    };

    const clear = () => {
        handle?.engine()?.removeOverlay(SOURCE_ID, LAYER_IDS);
        setText("");
        setError(null);
    };

    return (
        <SpaceBetween size="s">
            <Box variant="small">Paste GeoJSON to overlay it on the map.</Box>
            <Textarea
                value={text}
                onChange={({ detail }) => setText(detail.value)}
                placeholder='{"type":"FeatureCollection","features":[…]}'
                rows={10}
                invalid={!!error}
            />
            {error && <Box color="text-status-error" variant="small">{error}</Box>}
            <SpaceBetween direction="horizontal" size="xs">
                <Button variant="primary" disabled={!text.trim()} onClick={plot}>Plot</Button>
                <Button onClick={clear}>Clear</Button>
            </SpaceBetween>
        </SpaceBetween>
    );
};

export default GeoJsonPanel;
